import { useMemo, useState } from "react";
import { useGetList, useTranslate, type Identifier } from "ra-core";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

import {
  ListEmptyState,
  ListErrorState,
  ListRowsSkeleton,
} from "../design/ListStates";
import { Markdown } from "../misc/Markdown";
import { useGetSalesName } from "../sales/useGetSalesName";
import type { CultureDocumentVersion } from "../types";

const MAX_VERSIONS = 500;

/**
 * One "Versión N" row's date + author, "Carga inicial" per the ticket when
 * `created_by` is NULL (the seeded rows -- DATA CONTRACT EO (3)). Its own
 * component so the `useGetSalesName` hook (which itself calls `useGetMany`)
 * is only invoked when there IS an author to look up, never unconditionally
 * for a seeded row.
 */
const VersionAuthor = ({ createdBy }: { createdBy?: Identifier | null }) => {
  const translate = useTranslate();
  const name = useGetSalesName(createdBy ?? undefined, {
    enabled: createdBy != null,
  });
  return (
    <>{createdBy != null ? name : translate("crm.culture.history.seeded")}</>
  );
};

/**
 * Almikia Epic EO (TASK-100): "Historial" -- every version of one culture
 * document, newest first, each labelled "Versión N" with its date and
 * author ("Carga inicial" when `created_by` is NULL). Selecting a version
 * shows it read-only through the SAME sanitized {@link Markdown} renderer
 * the culture page and the editor's live preview use -- never a raw HTML
 * path for direccion-authored content shown to every role.
 */
export const CultureDocumentHistory = ({
  documentId,
  documentTitle,
  open,
  onOpenChange,
}: {
  documentId: Identifier;
  documentTitle: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const translate = useTranslate();
  const [selectedVersionId, setSelectedVersionId] = useState<Identifier | null>(
    null,
  );

  const { data, isPending, error, refetch } =
    useGetList<CultureDocumentVersion>(
      "culture_document_versions",
      {
        filter: { document_id: documentId },
        sort: { field: "version", order: "DESC" },
        pagination: { page: 1, perPage: MAX_VERSIONS },
      },
      // Only fetch while the dialog is actually open.
      { enabled: open },
    );

  const versions = useMemo(
    () => [...(data ?? [])].sort((a, b) => b.version - a.version),
    [data],
  );

  const selectedVersion =
    versions.find((version) => version.id === selectedVersionId) ?? null;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSelectedVersionId(null);
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg max-h-9/10 overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {translate("crm.culture.history.title", { title: documentTitle })}
          </DialogTitle>
        </DialogHeader>

        {selectedVersion ? (
          <div className="flex flex-col gap-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="min-h-11 self-start"
              onClick={() => setSelectedVersionId(null)}
            >
              {translate("crm.culture.history.back_to_list")}
            </Button>
            <h3 className="text-lg font-semibold">{selectedVersion.title}</h3>
            <Markdown className="text-base">
              {selectedVersion.body_markdown}
            </Markdown>
          </div>
        ) : isPending ? (
          <ListRowsSkeleton rows={3} columns={1} />
        ) : error ? (
          <ListErrorState
            message={translate("crm.common.list_error")}
            onRetry={refetch}
          />
        ) : versions.length === 0 ? (
          <ListEmptyState title={translate("crm.culture.history.empty")} />
        ) : (
          <ul className="flex flex-col gap-2">
            {versions.map((version) => (
              <li key={version.id}>
                <button
                  type="button"
                  onClick={() => setSelectedVersionId(version.id)}
                  className="flex w-full min-h-11 flex-col items-start gap-0.5 rounded-md border border-border px-3 py-2 text-left hover:bg-muted"
                >
                  <span className="font-medium">
                    {translate("crm.culture.history.version_number", {
                      number: version.version,
                    })}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {new Date(version.created_at).toLocaleDateString()} ·{" "}
                    <VersionAuthor createdBy={version.created_by} />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};
